/**
 * Theme: Light / Dark / System as a radio group (arrow keys move between the
 * options, one tab stop). "System" follows prefers-color-scheme live.
 */
import { useRef, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { useUiStore, type ThemePreference } from '../state/uiStore';
import { IconMonitor, IconMoon, IconSun } from './Icons';
import { Tooltip } from './Tooltip';
import './Settings.css';

const OPTIONS: { value: ThemePreference; label: string; Icon: typeof IconSun }[] = [
  { value: 'light', label: 'Light', Icon: IconSun },
  { value: 'dark', label: 'Dark', Icon: IconMoon },
  { value: 'system', label: 'System', Icon: IconMonitor },
];

export function ThemeToggle({ showLabels = false }: { showLabels?: boolean }) {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    e.stopPropagation();
    const next = (index + delta + OPTIONS.length) % OPTIONS.length;
    const option = OPTIONS[next];
    if (!option) return;
    setTheme(option.value);
    refs.current[next]?.focus();
  };

  return (
    <div className={clsx('segmented', showLabels && 'segmented--labels')} role="radiogroup" aria-label="Theme">
      {OPTIONS.map(({ value, label, Icon }, i) => (
        <Tooltip key={value} content={`${label} theme`} disabled={showLabels}>
          <button
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={theme === value}
            aria-label={showLabels ? undefined : `${label} theme`}
            tabIndex={theme === value ? 0 : -1}
            className="segmented__option"
            onClick={() => setTheme(value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <Icon />
            {showLabels && <span>{label}</span>}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
